

import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { ArchiveFile, ChatMessage, DBRow } from '../types';
import { editArticleWithAI, EditorBlockData } from '../services/geminiService';
import { fileToBase64, uploadMedia, getResizedImageUrl } from '../services/storageService';
import { getWikiArticle } from '../services/firestoreService';
import { FIREBASE_STORAGE_BASE_PATH } from '../constants';
import WikiHeader from './WikiHeader';

export interface EditorRef {
  save: () => Promise<boolean>;
  updateMocks: () => void;
}

interface EditorPageProps {
  onBack: () => void;
  onSave: (id: string, data: { title: string; content: string; category: string; imageUrl?: string; startYear?: number; endYear?: number }, isAutoSave: boolean) => Promise<boolean>;
  allFiles?: ArchiveFile[]; 
  user?: any;
  initialData?: { file: ArchiveFile; content: string } | null;
  isAdmin?: boolean;
  onSync?: () => void;
  isSyncing?: boolean;
  loadSource?: 'mock' | 'firestore';
  onMockUpdate?: () => void;
  onMenuClick?: () => void;
  isSidebarOpen?: boolean;
  sidebar?: React.ReactNode;
  onDirtyChange?: (isDirty: boolean) => void;
  articleImages?: string[]; 
  localDB?: Map<string, DBRow>;
}

// Extended Block Type
interface EditorBlock extends EditorBlockData {}

const EditableBlock = React.memo(({ html, onChange, placeholder, className, tagName = 'div' }: any) => {
  const contentEditableRef = useRef<HTMLElement>(null);
  
  // Only update innerHTML if it has effectively changed to avoid cursor jumps
  useEffect(() => {
    if (contentEditableRef.current && html !== contentEditableRef.current.innerHTML) {
        // Simple check to prevent aggressive overwrites during typing
        // Ideally we'd use a more complex diff or cursor preservation, 
        // but for this block-based editor, we assume 'html' prop updates only on blur or external change
        if (document.activeElement !== contentEditableRef.current) {
            contentEditableRef.current.innerHTML = html;
        }
    }
  }, [html]);

  const Tag = tagName as React.ElementType;

  return (
    <Tag
      ref={contentEditableRef as any}
      className={`${className} outline-none focus:bg-amber-50/50 dark:focus:bg-amber-900/10 transition-colors rounded px-2 -mx-2 min-h-[1.5em] empty:before:content-[attr(data-placeholder)] empty:before:text-stone-300 dark:empty:before:text-stone-600`}
      contentEditable
      suppressContentEditableWarning
      onBlur={(e: any) => onChange(e.currentTarget.innerHTML)} // Update on blur to be safe
      data-placeholder={placeholder}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

const GalleryBlock: React.FC<{
    blockId: string;
    content: string; 
    onContentChange: (id: string, newContent: string) => void;
    articleId: string;
}> = ({ blockId, content, onContentChange, articleId }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [urls, setUrls] = useState<string[]>([]);
    
    useEffect(() => {
        try {
            const parsedUrls = JSON.parse(content);
            if (Array.isArray(parsedUrls)) {
                setUrls(parsedUrls);
            }
        } catch (e) {
            setUrls([]);
        }
    }, [content]);

    const handleAddImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const preview = await fileToBase64(file, 512); 
            const newUrlsWithPreview = [...urls, preview];
            setUrls(newUrlsWithPreview);
            onContentChange(blockId, JSON.stringify(newUrlsWithPreview));

            const path = `${FIREBASE_STORAGE_BASE_PATH}wiki/${articleId}/gallery/${Date.now()}_${file.name}`;
            const storagePath = await uploadMedia(file, path); 
            
            setUrls(prev => {
                const finalUrls = prev.map(u => u === preview ? storagePath : u);
                onContentChange(blockId, JSON.stringify(finalUrls));
                return finalUrls;
            });
        } catch (err: any) {
            console.error("Image upload failed:", err.message || err);
            // Revert on failure
            setUrls(urls); 
            onContentChange(blockId, JSON.stringify(urls));
        }
        if(e.target) e.target.value = ''; 
    };

    const handleRemoveImage = (urlToRemove: string) => {
        const newUrls = urls.filter(u => u !== urlToRemove);
        setUrls(newUrls);
        onContentChange(blockId, JSON.stringify(newUrls));
    };

    return (
        <div className="bg-stone-50 dark:bg-stone-800/50 p-4 rounded-xl border border-stone-200 dark:border-stone-700 my-4">
            <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleAddImage} />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {urls.map((pathOrUrl, index) => (
                    <div key={`${pathOrUrl}-${index}`} className="relative group aspect-square rounded-lg overflow-hidden border border-stone-200 dark:border-stone-700">
                        <img src={getResizedImageUrl(pathOrUrl, '300x300')} alt={`Gallery item ${index + 1}`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button onClick={() => handleRemoveImage(pathOrUrl)} className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center hover:bg-red-700 transition-colors shadow-md">
                                <i className="fa-solid fa-trash-can text-xs"></i>
                            </button>
                        </div>
                    </div>
                ))}
                <button onClick={() => fileInputRef.current?.click()} className="aspect-square rounded-lg border-2 border-dashed border-stone-300 dark:border-stone-600 flex flex-col items-center justify-center text-stone-400 dark:text-stone-500 hover:bg-stone-100 dark:hover:bg-stone-700/50 hover:border-amber-500 hover:text-amber-500 transition-all">
                    <i className="fa-solid fa-plus text-2xl"></i>
                    <span className="text-xs font-bold mt-2">Add Image</span>
                </button>
            </div>
        </div>
    );
};

// Helper to extract YouTube video ID from URL
const getYouTubeId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
  const match = url.match(regExp);
  return (match && match[2].length === 11) ? match[2] : null;
};

export const EditorPage = forwardRef<EditorRef, EditorPageProps>(({ 
  onBack, 
  onSave, 
  allFiles = [], 
  user, 
  initialData,
  isAdmin = false,
  onSync,
  isSyncing = false,
  loadSource = 'mock',
  onMockUpdate,
  onMenuClick,
  isSidebarOpen,
  sidebar,
  onDirtyChange,
  articleImages = [],
  localDB
}, ref) => {
  const [title, setTitle] = useState('');
  const [blocks, setBlocks] = useState<EditorBlock[]>([{ id: `b-${Date.now()}`, type: 'text', content: '' }]);
  const [mainCategory, setMainCategory] = useState('');
  const [startYear, setStartYear] = useState<number | undefined>();
  const [endYear, setEndYear] = useState<number | undefined>();
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'unsaved' | 'error'>('saved');
  const [coverImage, setCoverImage] = useState<string | null>(null);
  
  const [isCatDropdownOpen, setIsCatDropdownOpen] = useState(false);
  const [catSearch, setCatSearch] = useState('');
  const catRef = useRef<HTMLDivElement>(null);

  const [activeMenuIndex, setActiveMenuIndex] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [mediaModalState, setMediaModalState] = useState<{
      isOpen: boolean;
      target: 'cover' | 'block';
      blockId?: string;
  }>({ isOpen: false, target: 'cover' });
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaUrlInput, setMediaUrlInput] = useState('');
  const mediaFileInputRef = useRef<HTMLInputElement>(null);

  const [showAIChat, setShowAIChat] = useState(() => window.innerWidth >= 768); 
  const [chatInput, setChatInput] = useState('');
  const [chatHistory, setChatHistory] = useState<{ role: 'user' | 'ai'; text: string }[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  
  const draftId = useRef(initialData?.file.id || `local-${Date.now()}`).current;
  const isInitialized = useRef(false);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
        if (catRef.current && !catRef.current.contains(event.target as Node)) {
            setIsCatDropdownOpen(false);
        }
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
            setActiveMenuIndex(null);
        }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const updateBlockContent = useCallback((id: string, newContent: string) => {
    setBlocks(prevBlocks => prevBlocks.map(block => 
        block.id === id ? { ...block, content: newContent } : block
    ));
    setSaveStatus('unsaved');
    if (onDirtyChange) onDirtyChange(true);
  }, [onDirtyChange]);

  const handleSave = useCallback(async (isAutoSave: boolean = false): Promise<boolean> => {
    setSaveStatus('saving');
    const fullContent = blocks.map(block => {
        if (block.type === 'text') {
            return block.content;
        } else if (block.type === 'image') {
            return `<img src="${block.content}" alt="Article image" />`;
        } else if (block.type === 'video') {
            const youtubeId = getYouTubeId(block.content);
            if (youtubeId) {
                return `<div class="video-container"><iframe src="https://www.youtube.com/embed/${youtubeId}" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe></div>`;
            }
            return `<a href="${block.content}" target="_blank">${block.content}</a>`;
        } else if (block.type === 'gallery') {
            try {
                const urls = JSON.parse(block.content);
                if (Array.isArray(urls)) {
                    const galleryHtml = urls.map(url => `<img src="${getResizedImageUrl(url, '1000x1000')}" alt="Gallery image" />`).join('');
                    return `<div class="editor-gallery">${galleryHtml}</div>`;
                }
            } catch (e) {
                console.error("Failed to parse gallery content:", e);
            }
            return '';
        } else if (block.type === 'divider') {
            return '<hr />';
        }
        return '';
    }).join('\n');

    const success = await onSave(draftId, {
      title,
      content: fullContent,
      category: mainCategory,
      imageUrl: coverImage || undefined,
      startYear,
      endYear,
    }, isAutoSave);

    setSaveStatus(success ? 'saved' : 'error');
    if (success && !isAutoSave && onDirtyChange) onDirtyChange(false);
    return success;
  }, [title, blocks, mainCategory, coverImage, onSave, draftId, onDirtyChange]);

  const handleMockUpdateClick = useCallback(() => {
    if (onMockUpdate) {
      onMockUpdate();
    }
  }, [onMockUpdate]);

  useImperativeHandle(ref, () => ({
    save: () => handleSave(false),
    updateMocks: handleMockUpdateClick
  }));

  // Initialize Data
  useEffect(() => {
    if (isInitialized.current) return;
    isInitialized.current = true;

    if (initialData?.file) {
        setTitle(initialData.file.name);
        setMainCategory(initialData.file.category || '');
        setCoverImage(initialData.file.imageUrl || null);
        setStartYear(initialData.file.startYear);
        setEndYear(initialData.file.endYear);
        
        let contentToParse = initialData.content || initialData.file.content || "";
        
        if (!contentToParse) {
            // Fetch if empty (unlikely with new architecture but safe fallback)
            getWikiArticle(initialData.file.id).then(data => {
                if (data && data.content) {
                    contentToParse = data.content;
                    parseAndSetBlocks(contentToParse);
                } else {
                    // Default block if no content
                    setBlocks([{ id: `b-${Date.now()}`, type: 'text', content: '' }]);
                }
            });
        } else {
            parseAndSetBlocks(contentToParse);
        }
    } else {
        // Fallback for completely new file
        setBlocks([{ id: `b-${Date.now()}`, type: 'text', content: '' }]);
    }
  }, [initialData]);

  const parseAndSetBlocks = (html: string) => {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newBlocks: EditorBlock[] = [];

      let currentTextBuffer: string[] = [];

      const flushText = () => {
          if (currentTextBuffer.length > 0) {
              newBlocks.push({
                  id: `b-init-${newBlocks.length}`,
                  type: 'text',
                  content: currentTextBuffer.join('')
              });
              currentTextBuffer = [];
          }
      };

      Array.from(doc.body.childNodes).forEach((node) => {
          if (node.nodeType === Node.TEXT_NODE) {
               if (node.textContent?.trim()) {
                   // Wrap raw text in p tag for consistency if it's orphaned
                   currentTextBuffer.push(`<p>${node.textContent}</p>`); 
               }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as HTMLElement;
              
              if (el.tagName === 'IMG') {
                  flushText();
                  newBlocks.push({
                      id: `b-init-${newBlocks.length}`,
                      type: 'image',
                      content: el.getAttribute('src') || ''
                  });
              } else if (el.tagName === 'HR') {
                  flushText();
                  newBlocks.push({
                      id: `b-init-${newBlocks.length}`,
                      type: 'divider',
                      content: ''
                  });
              } else if (el.classList.contains('video-container')) {
                  flushText();
                  const iframe = el.querySelector('iframe');
                  const src = iframe?.getAttribute('src') || '';
                  newBlocks.push({
                      id: `b-init-${newBlocks.length}`,
                      type: 'video',
                      content: src
                  });
              } else if (el.classList.contains('editor-gallery')) {
                  flushText();
                  const images = Array.from(el.querySelectorAll('img')).map(img => img.getAttribute('src') || '');
                  newBlocks.push({
                      id: `b-init-${newBlocks.length}`,
                      type: 'gallery',
                      content: JSON.stringify(images)
                  });
              } else {
                  // It's likely a standard text element (p, h1, ul, etc.)
                  currentTextBuffer.push(el.outerHTML);
              }
          }
      });
      flushText();

      if (newBlocks.length === 0) {
          newBlocks.push({ id: `b-${Date.now()}`, type: 'text', content: '' });
      }
      setBlocks(newBlocks);
  };

  const handleAddBlock = (index: number, type: EditorBlock['type'] = 'text') => {
      const newBlock: EditorBlock = {
          id: `b-${Date.now()}`,
          type,
          content: type === 'gallery' ? '[]' : ''
      };
      const newBlocks = [...blocks];
      newBlocks.splice(index + 1, 0, newBlock);
      setBlocks(newBlocks);
      setSaveStatus('unsaved');
      if (onDirtyChange) onDirtyChange(true);
      setActiveMenuIndex(null);
  };

  const handleRemoveBlock = (id: string) => {
      if (blocks.length <= 1) return;
      setBlocks(prev => prev.filter(b => b.id !== id));
      setSaveStatus('unsaved');
      if (onDirtyChange) onDirtyChange(true);
  };

  const handleMoveBlock = (index: number, direction: -1 | 1) => {
      if ((index === 0 && direction === -1) || (index === blocks.length - 1 && direction === 1)) return;
      const newBlocks = [...blocks];
      const temp = newBlocks[index];
      newBlocks[index] = newBlocks[index + direction];
      newBlocks[index + direction] = temp;
      setBlocks(newBlocks);
      setSaveStatus('unsaved');
      if (onDirtyChange) onDirtyChange(true);
  };

  const openMediaModal = (target: 'cover' | 'block', blockId?: string) => {
      setMediaModalState({ isOpen: true, target, blockId });
      setMediaUrlInput('');
      setMediaUploading(false);
  };

  const handleApplyMediaUrl = () => {
      if (!mediaUrlInput) return;
      if (mediaModalState.target === 'cover') {
          setCoverImage(mediaUrlInput);
      } else if (mediaModalState.blockId) {
          updateBlockContent(mediaModalState.blockId, mediaUrlInput);
      }
      setMediaModalState({ isOpen: false, target: 'cover' });
      setSaveStatus('unsaved');
      if (onDirtyChange) onDirtyChange(true);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      
      setMediaUploading(true);
      try {
          // Optimistic
          const localUrl = await fileToBase64(file);
          if (mediaModalState.target === 'cover') {
              setCoverImage(localUrl);
          } else if (mediaModalState.blockId) {
              updateBlockContent(mediaModalState.blockId, localUrl);
          }

          // Upload
          const path = `wiki/${draftId}/${mediaModalState.target}_${Date.now()}_${file.name}`;
          const storagePath = await uploadMedia(file, path);

          // Update with remote URL
          if (mediaModalState.target === 'cover') {
              setCoverImage(storagePath);
          } else if (mediaModalState.blockId) {
              updateBlockContent(mediaModalState.blockId, storagePath);
          }
          setMediaModalState({ isOpen: false, target: 'cover' });
          setSaveStatus('unsaved');
          if (onDirtyChange) onDirtyChange(true);
      } catch (e: any) {
          console.error("Upload failed", e.message || e);
      } finally {
          setMediaUploading(false);
      }
  };

  const handleChatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || isTyping) return;

    const userMsg = chatInput;
    setChatHistory(prev => [...prev, { role: 'user', text: userMsg }]);
    setChatInput('');
    setIsTyping(true);

    try {
        const response = await editArticleWithAI(userMsg, blocks, initialData?.file, localDB);
        setChatHistory(prev => [...prev, { role: 'ai', text: response.summary }]);
        
        if (response.blocks) {
            setBlocks(response.blocks);
            setSaveStatus('unsaved');
            if (onDirtyChange) onDirtyChange(true);
        }
    } catch (e: any) {
        setChatHistory(prev => [...prev, { role: 'ai', text: "Error: " + (e.message || "Failed to process request.") }]);
    } finally {
        setIsTyping(false);
    }
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatHistory, isTyping]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-stone-900 overflow-hidden">
        {/* Hidden File Input */}
        <input type="file" ref={mediaFileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />

        {/* Media Modal */}
        {mediaModalState.isOpen && (
            <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center pt-0 px-4 pb-4" onClick={() => !mediaUploading && setMediaModalState(prev => ({ ...prev, isOpen: false }))}>
                <div className="bg-white dark:bg-stone-800 rounded-xl shadow-2xl w-full max-w-lg p-6 animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
                    <h3 className="font-bold text-lg mb-4 text-stone-800 dark:text-stone-200">Select Media</h3>
                    
                    {/* Existing Assets Gallery (Simple grid of recently used or article images) */}
                    {articleImages.length > 0 && (
                        <div className="mb-6">
                            <label className="text-xs font-bold text-stone-400 uppercase mb-2 block">Article Assets</label>
                            <div className="grid grid-cols-4 gap-2">
                                {articleImages.map((src, i) => (
                                    <button key={i} onClick={() => { 
                                        if(mediaModalState.target === 'cover') setCoverImage(src); 
                                        else if(mediaModalState.blockId) updateBlockContent(mediaModalState.blockId, src);
                                        setMediaModalState(prev => ({ ...prev, isOpen: false }));
                                    }} className="aspect-square rounded overflow-hidden border border-stone-200 hover:border-amber-500 transition-all">
                                        <img src={getResizedImageUrl(src, '300x300')} className="w-full h-full object-cover" alt="asset" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        <button 
                            onClick={() => mediaFileInputRef.current?.click()}
                            disabled={mediaUploading}
                            className="w-full py-3 border-2 border-dashed border-stone-300 dark:border-stone-600 rounded-lg text-stone-500 hover:text-amber-500 hover:border-amber-500 transition-colors flex items-center justify-center gap-2"
                        >
                            <i className={`fa-solid ${mediaUploading ? 'fa-spinner animate-spin' : 'fa-upload'}`}></i>
                            {mediaUploading ? 'Uploading...' : 'Upload from Device'}
                        </button>
                        
                        <div className="flex gap-2">
                            <input 
                                type="text" 
                                value={mediaUrlInput}
                                onChange={e => setMediaUrlInput(e.target.value)}
                                placeholder="Or paste image/video URL..."
                                className="flex-1 border border-stone-200 dark:border-stone-600 rounded-lg px-3 py-2 bg-stone-50 dark:bg-stone-900 text-stone-800 dark:text-stone-200 text-sm"
                            />
                            <button onClick={handleApplyMediaUrl} className="px-4 bg-amber-500 text-white rounded-lg font-bold text-sm hover:bg-amber-600">Apply</button>
                        </div>
                    </div>
                    <button onClick={() => setMediaModalState(prev => ({ ...prev, isOpen: false }))} className="mt-4 text-xs text-stone-400 hover:text-stone-600 underline w-full text-center">Cancel</button>
                </div>
            </div>
        )}

        <WikiHeader 
            title={title || "Untitled Article"}
            onBack={onBack}
            onMenuClick={onMenuClick}
            isSidebarOpen={isSidebarOpen}
            saveStatus={saveStatus}
            onSync={() => handleSave(false)}
            isSyncing={isSyncing}
            rightAction={
                <button onClick={() => setShowAIChat(!showAIChat)} className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${showAIChat ? 'bg-indigo-100 text-indigo-600' : 'bg-stone-100 text-stone-400 hover:text-indigo-600'}`}>
                    <i className="fa-solid fa-wand-magic-sparkles"></i>
                </button>
            }
        />

        <div className="flex-1 flex overflow-hidden relative">
            {sidebar}

            {/* Main Editor Area */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-12 relative">
                <div className="max-w-3xl mx-auto pb-32">
                    {/* Cover Image */}
                    <div className="relative group mb-8 rounded-2xl overflow-hidden bg-stone-100 dark:bg-stone-800 min-h-[200px] border border-stone-200 dark:border-stone-700">
                        {coverImage ? (
                            <img src={getResizedImageUrl(coverImage, '1800x1800')} alt="Cover" className="w-full h-64 md:h-96 object-cover" />
                        ) : (
                            <div className="h-48 flex items-center justify-center text-stone-400 flex-col gap-2">
                                <i className="fa-regular fa-image text-3xl"></i>
                                <span className="text-xs uppercase font-bold tracking-widest">Add Cover Image</span>
                            </div>
                        )}
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                            <button onClick={() => openMediaModal('cover')} className="bg-white/20 backdrop-blur px-4 py-2 rounded-full text-white font-bold text-sm hover:bg-white/30 transition-colors flex items-center gap-2">
                                <i className="fa-solid fa-camera"></i> {coverImage ? 'Change Cover' : 'Add Cover'}
                            </button>
                        </div>
                    </div>

                    {/* Metadata Inputs */}
                    <input 
                        type="text" 
                        value={title} 
                        onChange={(e) => { setTitle(e.target.value); setSaveStatus('unsaved'); if(onDirtyChange) onDirtyChange(true); }}
                        placeholder="Article Title"
                        className="w-full text-4xl md:text-5xl font-serif font-bold text-stone-900 dark:text-stone-100 bg-transparent border-none outline-none placeholder:text-stone-300 dark:placeholder:text-stone-700 mb-6"
                    />

                    <div className="flex flex-wrap items-center gap-6 mb-8 text-sm relative" ref={catRef}>
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
                                <i className="fa-solid fa-folder"></i>
                                <span className="uppercase font-bold text-xs tracking-wider">Category:</span>
                            </div>
                            <div className="relative">
                                <button onClick={() => setIsCatDropdownOpen(!isCatDropdownOpen)} className="font-bold text-amber-600 hover:text-amber-700 dark:text-amber-500">
                                    {mainCategory || "Uncategorized"} <i className="fa-solid fa-caret-down ml-1"></i>
                                </button>
                                {isCatDropdownOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-stone-800 rounded-xl shadow-xl border border-stone-200 dark:border-stone-700 z-20 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                        <div className="p-2 border-b border-stone-100 dark:border-stone-700">
                                            <input 
                                                type="text" 
                                                value={catSearch}
                                                onChange={e => setCatSearch(e.target.value)}
                                                placeholder="New or existing..."
                                                className="w-full bg-stone-50 dark:bg-stone-900 px-3 py-1.5 rounded-lg text-xs outline-none"
                                                autoFocus
                                            />
                                        </div>
                                        <div className="max-h-48 overflow-y-auto">
                                            {[...new Set(allFiles.map(f => f.category))].filter(c => c && c.toLowerCase().includes(catSearch.toLowerCase())).map(cat => (
                                                <button 
                                                    key={cat}
                                                    onClick={() => { setMainCategory(cat!); setIsCatDropdownOpen(false); setSaveStatus('unsaved'); if(onDirtyChange) onDirtyChange(true); }}
                                                    className="w-full text-left px-4 py-2 hover:bg-stone-50 dark:hover:bg-stone-700/50 text-sm text-stone-700 dark:text-stone-300"
                                                >
                                                    {cat}
                                                </button>
                                            ))}
                                            {catSearch && (
                                                <button 
                                                    onClick={() => { setMainCategory(catSearch); setIsCatDropdownOpen(false); setSaveStatus('unsaved'); if(onDirtyChange) onDirtyChange(true); }}
                                                    className="w-full text-left px-4 py-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-sm text-amber-600 font-bold"
                                                >
                                                    Create "{catSearch}"
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="h-4 w-px bg-stone-200 dark:bg-stone-700 hidden md:block"></div>

                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-stone-500 dark:text-stone-400">
                                <i className="fa-solid fa-timeline"></i>
                                <span className="uppercase font-bold text-xs tracking-wider">Epoch:</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <input 
                                    type="number" 
                                    placeholder="Start Year"
                                    value={startYear === undefined ? '' : startYear}
                                    onChange={(e) => { 
                                        const val = e.target.value === '' ? undefined : parseInt(e.target.value);
                                        setStartYear(val);
                                        setSaveStatus('unsaved');
                                        if(onDirtyChange) onDirtyChange(true);
                                    }}
                                    className="w-24 bg-stone-100 dark:bg-stone-800 px-2 py-1 rounded border border-stone-200 dark:border-stone-700 text-xs font-bold"
                                />
                                <span className="text-stone-400">to</span>
                                <input 
                                    type="number" 
                                    placeholder="End Year"
                                    value={endYear === undefined ? '' : endYear}
                                    onChange={(e) => { 
                                        const val = e.target.value === '' ? undefined : parseInt(e.target.value);
                                        setEndYear(val);
                                        setSaveStatus('unsaved');
                                        if(onDirtyChange) onDirtyChange(true);
                                    }}
                                    className="w-24 bg-stone-100 dark:bg-stone-800 px-2 py-1 rounded border border-stone-200 dark:border-stone-700 text-xs font-bold"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Editor Blocks */}
                    <div className="space-y-4" ref={menuRef}>
                        {blocks.map((block, index) => (
                            <div key={block.id} className="group relative pl-8 md:pl-12 transition-all">
                                {/* Block Controls Hover */}
                                <div className="absolute left-0 top-0 bottom-0 w-8 md:w-12 flex flex-col items-center pt-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => setActiveMenuIndex(activeMenuIndex === index ? null : index)} className="w-6 h-6 flex items-center justify-center text-stone-300 hover:text-stone-600 dark:hover:text-stone-200 rounded">
                                        <i className="fa-solid fa-grip-vertical"></i>
                                    </button>
                                    
                                    {/* Context Menu */}
                                    {activeMenuIndex === index && (
                                        <div className="absolute left-full top-0 ml-2 bg-white dark:bg-stone-800 rounded-lg shadow-xl border border-stone-200 dark:border-stone-700 py-1 min-w-[140px] z-20 animate-in fade-in zoom-in-95 duration-150">
                                            <button onClick={() => handleAddBlock(index, 'text')} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-solid fa-paragraph w-4"></i> Text</button>
                                            <button onClick={() => handleAddBlock(index, 'image')} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-regular fa-image w-4"></i> Image</button>
                                            <button onClick={() => handleAddBlock(index, 'gallery')} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-solid fa-images w-4"></i> Gallery</button>
                                            <button onClick={() => handleAddBlock(index, 'video')} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-brands fa-youtube w-4"></i> Video</button>
                                            <button onClick={() => handleAddBlock(index, 'divider')} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-solid fa-minus w-4"></i> Divider</button>
                                            <div className="h-px bg-stone-100 dark:bg-stone-700 my-1"></div>
                                            <button onClick={() => handleMoveBlock(index, -1)} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-solid fa-arrow-up w-4"></i> Move Up</button>
                                            <button onClick={() => handleMoveBlock(index, 1)} className="w-full text-left px-3 py-2 text-xs hover:bg-stone-50 dark:hover:bg-stone-700 text-stone-600 dark:text-stone-300 flex items-center gap-2"><i className="fa-solid fa-arrow-down w-4"></i> Move Down</button>
                                            <div className="h-px bg-stone-100 dark:bg-stone-700 my-1"></div>
                                            <button onClick={() => handleRemoveBlock(block.id)} className="w-full text-left px-3 py-2 text-xs hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 flex items-center gap-2"><i className="fa-solid fa-trash w-4"></i> Delete</button>
                                        </div>
                                    )}
                                </div>

                                {/* Content Renderers */}
                                {block.type === 'text' && (
                                    <EditableBlock 
                                        html={block.content} 
                                        onChange={(html: string) => updateBlockContent(block.id, html)}
                                        placeholder="Type something..."
                                        className="prose prose-stone dark:prose-invert max-w-none text-lg leading-relaxed"
                                    />
                                )}

                                {block.type === 'image' && (
                                    <div className="relative group/image">
                                        {block.content ? (
                                            <img src={getResizedImageUrl(block.content, '1000x1000')} className="w-full rounded-xl shadow-sm" alt="content" />
                                        ) : (
                                            <div className="w-full h-48 bg-stone-100 dark:bg-stone-800 rounded-xl flex items-center justify-center border-2 border-dashed border-stone-200 dark:border-stone-700">
                                                <span className="text-stone-400 text-sm font-bold uppercase">Image Placeholder</span>
                                            </div>
                                        )}
                                        <button onClick={() => openMediaModal('block', block.id)} className="absolute top-2 right-2 bg-black/60 text-white text-xs px-3 py-1.5 rounded-full opacity-0 group-hover/image:opacity-100 transition-opacity">
                                            <i className="fa-solid fa-camera mr-1"></i> Edit
                                        </button>
                                    </div>
                                )}

                                {block.type === 'gallery' && (
                                    <GalleryBlock 
                                        blockId={block.id} 
                                        content={block.content} 
                                        onContentChange={updateBlockContent} 
                                        articleId={draftId} 
                                    />
                                )}

                                {block.type === 'video' && (
                                    <div className="relative group/video p-4 bg-stone-50 dark:bg-stone-800 rounded-xl border border-stone-200 dark:border-stone-700">
                                        <input 
                                            type="text" 
                                            value={block.content}
                                            onChange={(e) => updateBlockContent(block.id, e.target.value)}
                                            placeholder="Paste YouTube URL..."
                                            className="w-full bg-transparent border-none outline-none text-stone-600 dark:text-stone-300 text-sm font-mono"
                                        />
                                        {getYouTubeId(block.content) && (
                                            <div className="mt-4 aspect-video rounded-lg overflow-hidden bg-black">
                                                <iframe src={`https://www.youtube.com/embed/${getYouTubeId(block.content)}`} className="w-full h-full" frameBorder="0" allowFullScreen></iframe>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {block.type === 'divider' && (
                                    <div className="py-8 flex items-center justify-center">
                                        <div className="h-px bg-stone-200 dark:bg-stone-700 w-24"></div>
                                        <div className="text-stone-300 dark:text-stone-600 mx-4 text-xl">***</div>
                                        <div className="h-px bg-stone-200 dark:bg-stone-700 w-24"></div>
                                    </div>
                                )}
                            </div>
                        ))}
                        
                        {/* Quick Add at Bottom */}
                        <div className="pl-12 pt-4 opacity-50 hover:opacity-100 transition-opacity flex gap-2">
                            <button onClick={() => handleAddBlock(blocks.length - 1, 'text')} className="bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-500 rounded-full px-4 py-2 text-xs font-bold transition-colors">
                                <i className="fa-solid fa-plus mr-1"></i> Add Text
                            </button>
                             <button onClick={() => handleAddBlock(blocks.length - 1, 'image')} className="bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 text-stone-500 rounded-full px-4 py-2 text-xs font-bold transition-colors">
                                <i className="fa-regular fa-image mr-1"></i> Add Image
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* AI Assistant Sidebar */}
            {showAIChat && (
                <div className="w-80 bg-stone-50 dark:bg-stone-900 border-l border-stone-200 dark:border-stone-800 flex flex-col shadow-xl z-20">
                    <div className="p-4 border-b border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900">
                        <h4 className="font-bold text-indigo-600 dark:text-indigo-400 text-xs uppercase tracking-wider flex items-center gap-2">
                            <i className="fa-solid fa-wand-magic-sparkles"></i> AI Co-Editor
                        </h4>
                    </div>
                    <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                        {chatHistory.length === 0 && (
                            <div className="text-center py-8 text-stone-400">
                                <p className="text-sm">I can help you rewrite sections, fix grammar, or suggest structure.</p>
                            </div>
                        )}
                        {chatHistory.map((msg, i) => (
                            <div key={i} className={`p-3 rounded-lg text-sm ${msg.role === 'user' ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-900 dark:text-indigo-100 ml-4' : 'bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 mr-4'}`}>
                                {msg.text}
                            </div>
                        ))}
                        {isTyping && <div className="text-xs text-stone-400 animate-pulse pl-2">Thinking...</div>}
                        <div ref={chatEndRef}></div>
                    </div>
                    <div className="p-3 border-t border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900">
                        <form onSubmit={handleChatSubmit} className="relative">
                            <input 
                                type="text" 
                                value={chatInput}
                                onChange={e => setChatInput(e.target.value)}
                                placeholder="E.g., 'Summarize the second paragraph'..."
                                className="w-full bg-stone-100 dark:bg-stone-800 border-none rounded-lg py-2 pl-3 pr-10 text-xs focus:ring-1 focus:ring-indigo-500"
                            />
                            <button type="submit" disabled={isTyping || !chatInput} className="absolute right-2 top-1.5 text-indigo-500 hover:text-indigo-600 disabled:opacity-50">
                                <i className="fa-solid fa-paper-plane"></i>
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    </div>
  );
});

export default EditorPage;
